import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { Entity } from '../../shared/entity-search/entity-search.service';
import { EnvironmentService } from '../environment/environment.service';
import { SessionService } from '../../auth/session/session.service';
import { VIDEO_PROFILE_SETTING_NAME } from './video-profile';

@Injectable({
  providedIn: 'root',
})
export class SettingsService {
  settingsUpdateUrl: string = environment.settingsUpdate;
  deviceSettingsUrl: string = environment.deviceSettings;

  private settingTypes: Map<string, SettingType> = new Map<
    string,
    SettingType
  >();
  private settingTypesCache: Map<string, Promise<SettingType[]>> = new Map();

  constructor(
    private httpClient: HttpClient,
    private environmentService: EnvironmentService,
    private sessionService: SessionService
  ) {}

  getDeviceSettings(serialNumber: string): Promise<SettingResult[]> {
    return this.httpClient
      .get<
        SettingResult[]
      >(`${this.deviceSettingsUrl}/settings/device/${serialNumber}`)
      .toPromise();
  }

  updateDeviceSettings(
    serialNumber: string,
    request: UpdateDeviceSettingsRequest
  ): Promise<void> {
    return this.httpClient
      .post<void>(
        `${this.deviceSettingsUrl}/settings/device/${serialNumber}`,
        request
      )
      .toPromise();
  }

  /**
   * Dissociate HUB from a device by updating Unisyn settings
   */
  dissociateHub(serialNumber: string): Promise<void> {
    const request: UpdateDeviceSettingsRequest = {
      pushSettings: true,
      settings: [
        {
          name: 'Unisyn Assigned Follower 1',
          value: null
        },
        {
          name: 'Enable Unisyn Leader',
          value: 'False'
        }
      ]
    };
    return this.updateDeviceSettings(serialNumber, request);
  }

  getVehicleSettings(vehicleId: string): Promise<SettingResult[]> {
    return this.httpClient
      .get<
        SettingResult[]
      >(`${this.deviceSettingsUrl}/settings/vehicle/${vehicleId}`)
      .toPromise();
  }

  getGroupSettings(groupId: string): Promise<SettingResult[]> {
    return this.httpClient
      .get<
        SettingResult[]
      >(`${this.deviceSettingsUrl}/settings/group/${groupId}`)
      .toPromise();
  }

  getStackSettings(stackId: number): Promise<SettingResult[]> {
    return this.httpClient
      .get<
        SettingResult[]
      >(`${this.deviceSettingsUrl}/settings/root/${stackId}`)
      .toPromise();
  }

  getDefaultSettings(): Promise<SettingResult[]> {
    return this.httpClient
      .get<SettingResult[]>(`${this.deviceSettingsUrl}/settings/default`)
      .toPromise();
  }

  getSettingsUpdateRequests(query): Promise<SettingsUpdateRequest[]> {
    let params = new HttpParams();
    Object.entries(query).forEach(([key, value]) => {
      params = params.append(key, value as string | undefined);
    });

    return this.httpClient
      .get<
        SettingsUpdateRequest[]
      >(`${this.settingsUpdateUrl}/request`, { params: params })
      .toPromise();
  }

  getSettingsUpdateRequest(requestId: string): Promise<SettingsUpdateRequest> {
    return this.httpClient
      .get<SettingsUpdateRequest>(
        `${this.settingsUpdateUrl}/request/${requestId}`,
      )
      .toPromise();
  }

  reviewSettingsUpdateRequest(
    requestId: string,
    review: SettingReviewRequest,
  ): Promise<void> {
    return this.httpClient
      .post<void>(
        `${this.settingsUpdateUrl}/request/${requestId}/review`,
        review,
      )
      .toPromise();
  }

  createSettingsUpdateRequest(request: CreateSettingRequest): Promise<void> {
    return this.httpClient
      .post<void>(`${this.settingsUpdateUrl}/request`, request)
      .toPromise();
  }

  /**
   * Clears the setting types cache for all environments.
   * Call this when settings are created, updated, or promoted.
   */
  clearSettingTypesCache(): void {
    this.settingTypesCache.clear();
  }

  async initializeSettingTypes(): Promise<void> {
    const settingTypes = await this.fetchSettingTypesForEnvironment(this.deviceSettingsUrl);

    settingTypes
      .filter((st) => !st.isLegacy)
      .forEach((st) => this.settingTypes.set(st.name, st));

    this.registerVirtualSettingTypes();
  }

  /**
   * Registers UI-only "virtual" settings (e.g. the HD Video Profile grouping).
   * These are selectable in the UI but are NEVER submitted by name — they
   * expand into real, device-registered settings on submit. Registering them
   * here keeps getSettingType()/settingName/settingValue pipes from breaking.
   */
  private registerVirtualSettingTypes(): void {
    if (this.settingTypes.has(VIDEO_PROFILE_SETTING_NAME)) {
      return;
    }
    this.settingTypes.set(VIDEO_PROFILE_SETTING_NAME, {
      name: VIDEO_PROFILE_SETTING_NAME,
      label: VIDEO_PROFILE_SETTING_NAME,
      category: 'Video',
      defaultValue: '',
      dataType: DataType.string,
      minValue: '',
      maxValue: '',
      units: '',
      allowableValues: [],
      isLegacy: false,
    });
  }

  getSettingType(settingName: string): SettingType {
    return this.settingTypes.get(settingName);
  }

  /**
   * Resolves a setting's real registered Name (the dictionary/device key) from
   * an identifier that may be either the Name itself or the human Label.
   * The grid/UI shows Label, but the device + GetSettingType key on Name, and
   * the model decouples the two. Returns undefined when nothing matches.
   */
  resolveSettingName(identifier: string): string | undefined {
    if (this.settingTypes.has(identifier)) {
      return identifier;
    }
    const byLabel = Array.from(this.settingTypes.values()).find(
      (st) => st.label === identifier,
    );
    return byLabel?.name;
  }

  getAllSettingTypes(): SettingType[] {
    return Array.from(this.settingTypes.values());
  }

  /**
   * Submits a new setting type through the bulkupdate-api traceability workflow (LEVI-822).
   * In lower environments the change applies immediately and is audited; the workflow only
   * defers to approval in prod, which is reached via {@link promoteSetting}, not this method.
   */
  async createNewSetting(newSetting: NewSettingRequest): Promise<SettingTypeChangeSubmissionResult> {
    const response = await this.submitSettingTypeChange(this.settingsUpdateUrl, newSetting);
    this.clearSettingTypesCache();
    return response;
  }

  /**
   * Submits an update to an existing setting type through the bulkupdate-api workflow.
   * The setting is identified by its name; the backend derives that this is an update.
   */
  async updateSetting(setting: NewSettingRequest): Promise<SettingTypeChangeSubmissionResult> {
    const response = await this.submitSettingTypeChange(this.settingsUpdateUrl, setting);
    this.clearSettingTypesCache();
    return response;
  }

  /**
   * Check if a setting exists in the higher environment with identical values.
   * Returns true if the setting exists with the same values, false if it doesn't exist or has different values.
   */
  async checkSettingExistsInHigherEnvironment(setting: SettingType): Promise<boolean> {
    const higherEnvConfig = this.environmentService.getHigherEnvironmentConfig();
    if (!higherEnvConfig) {
      return false; // No higher environment (probably in prod)
    }

    try {
      const settingTypes = await this.fetchSettingTypesForEnvironment(higherEnvConfig.deviceSettings);
      
      const higherSetting = settingTypes?.find(st => st.name === setting.name);
      
      if (!higherSetting) {
        return false; // Setting doesn't exist in higher environment
      }
      
      // Compare all fields to see if they match
      const fieldsMatch =
        higherSetting.label === setting.label &&
        higherSetting.category === setting.category &&
        higherSetting.defaultValue === setting.defaultValue &&
        higherSetting.dataType === setting.dataType &&
        higherSetting.minValue === setting.minValue &&
        higherSetting.maxValue === setting.maxValue &&
        higherSetting.units === setting.units &&
        higherSetting.isLegacy === setting.isLegacy &&
        this.compareAllowableValues(higherSetting.allowableValues, setting.allowableValues) &&
        this.compareApplicableHardwarePlatforms(higherSetting.applicableHardwarePlatforms, setting.applicableHardwarePlatforms);
      
      return fieldsMatch;
    } catch (error) {
      console.error('Error checking setting in higher environment:', error);
      return false;
    }
  }

  /**
   * Promote a setting to the higher environment through the bulkupdate-api workflow.
   * A stg -> prod promotion is parked for approval (a second SettingTypeAdmin must approve via
   * {@link reviewSettingTypeChange}); a dev -> stg promotion applies immediately. The returned
   * result's `requiresApproval` flag tells the caller which happened.
   */
  async promoteSetting(setting: NewSettingRequest): Promise<SettingTypeChangeSubmissionResult> {
    const higherEnvConfig = this.environmentService.getHigherEnvironmentConfig();
    if (!higherEnvConfig) {
      throw new Error('No higher environment available for promotion');
    }

    const response = await this.submitSettingTypeChange(higherEnvConfig.settingsUpdate, setting);
    this.clearSettingTypesCache();
    return response;
  }

  /**
   * Approve or reject a pending setting-type change against the given bulkupdate-api base URL
   * (the higher environment's, for a stg -> prod promotion). The backend rejects self-approval.
   */
  reviewSettingTypeChange(
    baseUrl: string,
    requestId: string,
    review: SettingTypeChangeReview
  ): Promise<void> {
    return this.httpClient
      .post<void>(`${baseUrl}/settingtype/${requestId}/review`, review)
      .toPromise();
  }

  /**
   * Fetch setting-type changes awaiting approval from the given bulkupdate-api base URL.
   * Used to tag stg grid rows that have an open prod promotion.
   */
  getPendingSettingTypeChanges(baseUrl: string): Promise<PendingSettingTypeChange[]> {
    return this.httpClient
      .get<PendingSettingTypeChange[]>(`${baseUrl}/settingtype/pending`)
      .toPromise();
  }

  /**
   * Fetch the full audit trail for a single setting type from the current environment's
   * bulkupdate-api, newest-first. Each row captures who applied the change, who approved it (for
   * changes that went through the prod approval workflow), and the before/after definition snapshots.
   */
  getSettingTypeAudit(settingName: string): Promise<SettingTypeAuditRow[]> {
    return this.httpClient
      .get<SettingTypeAuditRow[]>(`${this.settingsUpdateUrl}/settingtype/${encodeURIComponent(settingName)}/audit`)
      .toPromise();
  }

  getCurrentUserName(): string {
    return this.sessionService.getCurrentSession()?.name ?? 'unknown';
  }

  getCurrentUserEmail(): string {
    return this.sessionService.getCurrentSession()?.email ?? 'unknown';
  }

  private submitSettingTypeChange(
    baseUrl: string,
    definition: NewSettingRequest
  ): Promise<SettingTypeChangeSubmissionResult> {
    const submission: SettingTypeChangeSubmission = {
      definition,
      requesterName: this.getCurrentUserName(),
      requesterEmail: this.getCurrentUserEmail(),
      description: `Setting type change for "${definition.name}"`,
    };
    return this.httpClient
      .post<SettingTypeChangeSubmissionResult>(`${baseUrl}/settingtype`, submission)
      .toPromise();
  }

  private compareApplicableHardwarePlatforms(arr1?: string[], arr2?: string[]): boolean {
    const normalized1 = arr1?.length ? [...arr1].sort() : [];
    const normalized2 = arr2?.length ? [...arr2].sort() : [];
    return JSON.stringify(normalized1) === JSON.stringify(normalized2);
  }

  /**
   * Compare two allowable values arrays for equality
   */
  private compareAllowableValues(arr1: AllowableValue[], arr2: AllowableValue[]): boolean {
    if (!arr1 && !arr2) return true;
    if (!arr1 || !arr2) return false;
    if (arr1.length !== arr2.length) return false;
    
    return arr1.every((val1, index) => {
      const val2 = arr2[index];
      return val1.label === val2.label && val1.value === val2.value;
    });
  }

    /**
   * Fetch setting types from a specific environment with caching.
   * Handles concurrent requests by caching the promise itself.
   */
  private async fetchSettingTypesForEnvironment(baseUrl: string): Promise<SettingType[]> {
    // Check if we already have a request in flight or cached
    if (this.settingTypesCache.has(baseUrl)) {
      return this.settingTypesCache.get(baseUrl);
    }

    // Create the promise and cache it immediately to handle concurrent requests
    const promise = this.httpClient
      .get<SettingType[]>(`${baseUrl}/settings/settingtypes`)
      .toPromise();

    this.settingTypesCache.set(baseUrl, promise);

    try {
      const result = await promise;
      return result;
    } catch (error) {
      // Remove from cache on error so it can be retried
      this.settingTypesCache.delete(baseUrl);
      throw error;
    }
  }
}

export interface NewSettingRequest {
  name: string;
  label: string;
  category: string;
  class: string;
  defaultValue: string;
  dataType: string;
  allowableValues: Array<{
    label: string;
    value: string;
  }>;
  minValue: string;
  maxValue: string;
  units: string;
  isLegacy: boolean;
  isExchangeable: boolean;
  applicableHardwarePlatforms?: string[];
}

export interface NewSettingResponse {
  id: string;
}

/**
 * Payload sent to bulkupdate-api POST /settingtype. `definition` carries the full setting-type
 * definition (a NewSettingRequest deserializes cleanly into the backend SettingType model).
 */
export interface SettingTypeChangeSubmission {
  definition: NewSettingRequest;
  requesterName: string;
  requesterEmail: string;
  description: string;
}

export interface SettingTypeChangeSubmissionResult {
  requestId?: string;
  status: string;
  requiresApproval: boolean;
}

export interface SettingTypeChangeReview {
  approved: boolean;
  reviewer: string;
  reviewerEmail: string;
  description: string;
}

export interface PendingSettingTypeChange {
  id: string;
  settingName: string;
  action: string;
  requester: string;
  requesterEmail?: string;
  status: string;
  description: string;
  requestDate: Date;
  proposedDefinition: SettingType;
  previousDefinition?: SettingType;
}

/**
 * One audited setting-type apply. beforeDefinition/afterDefinition are JSON strings of a
 * {@link SettingType} (beforeDefinition is absent for a Create). approvedBy is populated only for
 * changes that went through the prod approval workflow; direct lower-env applies leave it undefined.
 */
export interface SettingTypeAuditRow {
  id: string;
  settingName: string;
  action: string; // 'Create' | 'Update'
  changedBy: string;
  changedByEmail?: string;
  approvedBy?: string;
  approvedByEmail?: string;
  beforeDefinition?: string;
  afterDefinition: string;
  environment: string;
  appliedDate: string;
  requestId?: string;
}

export interface SettingsUpdateRequest {
  id: string;
  entities: EntityRef[];
  requester: string;
  description: string;
  settings: Setting[];
  status: string;
  requestDate: Date;
  completionDate: Date;
  reviews: SettingReview[];
}

export interface Setting {
  name: string;
  value: string;
  displayValue: string;
}

export interface SettingReview {
  approved: boolean;
  reviewer: string;
  description: string;
  requestId: string;
  reviewDate: Date;
}

export interface SettingReviewRequest {
  approved: boolean;
  reviewer: string;
  description: string;
}

export interface SettingType {
  name: string;
  label: string;
  category: string;
  defaultValue: string;
  dataType: DataType;
  minValue: string;
  maxValue: string;
  units: string;
  allowableValues: AllowableValue[];
  isLegacy: boolean;
  applicableHardwarePlatforms?: string[];
}

export interface AllowableValue {
  label: string;
  value: string;
}

export interface CreateSettingRequest {
  settings: Setting[];
  requesterName: string;
  entities: Entity[];
  description: string;
}

export interface EntityRef {
  name: string;
  id: string;
  entityType: EntityType;
}

export interface SettingResults {
  hierarchy: string[];
  settings: SettingResult;
}

export interface SettingResult {
  name: string;
  value: string;
  lastKnownValue: string;
  isPending: boolean;
  dataType: DataType;
  authoritativeEntityType: EntityType;
  authoritativeEntityId: string;
  category: string;
}

export enum EntityType {
  company = 'Company',
  stack = 'Root',
  group = 'Group',
  device = 'Device',
  vehicle = 'Vehicle',
  default = 'Default',

  // Makes up for the difference between service domains.
  // DeviceSettings uses "Root" and EntitySearch uses "Stack"
  stack_deprecated = 'Stack',
}

export enum DataType {
  boolean = 'Boolean',
  string = 'String',
  int32 = 'Int32',
  int64 = 'Int64',
  decimal = 'Decimal',
}

export interface UpdateDeviceSettingsRequest {
  pushSettings: boolean;
  settings: UpdateSettingItem[];
}

export interface UpdateSettingItem {
  name: string;
  value: string | null;
}
